/**
 * Código do país do telefone.
 *
 * O sistema nasceu só com número brasileiro: o campo guardava DDD + número e
 * o "55" era grudado na hora de mandar o WhatsApp. Famílias estrangeiras não
 * cabiam nisso — e pior, um "+1 415 555 2671" tem onze dígitos e virava
 * "5514155552671", um celular brasileiro que existe e é de outra pessoa.
 *
 * Agora o país é escolhido, guardado ao lado do número e usado no envio.
 * **Vazio continua valendo Brasil**, que é o que todas as visitas anteriores a
 * este campo são.
 */

export const PAIS_PADRAO = "55";

export interface Pais {
  /** Código de discagem, só dígitos. */
  codigo: string;
  nome: string;
  /** Sigla ISO — serve de chave no select, já que o código se repete. */
  sigla: string;
}

/**
 * Lista curada, não a ONU inteira: Brasil no topo e depois os países de onde
 * vêm, na prática, as famílias da região — o polo aeroespacial de São José dos
 * Campos traz muita gente dos Estados Unidos, da Europa e do Japão.
 *
 * Códigos repetidos são normais (+1 serve Estados Unidos e Canadá); por isso a
 * chave é a sigla.
 */
export const PAISES: Pais[] = [
  { codigo: "55", nome: "Brasil", sigla: "BR" },
  { codigo: "27", nome: "África do Sul", sigla: "ZA" },
  { codigo: "49", nome: "Alemanha", sigla: "DE" },
  { codigo: "244", nome: "Angola", sigla: "AO" },
  { codigo: "54", nome: "Argentina", sigla: "AR" },
  { codigo: "61", nome: "Austrália", sigla: "AU" },
  { codigo: "43", nome: "Áustria", sigla: "AT" },
  { codigo: "32", nome: "Bélgica", sigla: "BE" },
  { codigo: "591", nome: "Bolívia", sigla: "BO" },
  { codigo: "1", nome: "Canadá", sigla: "CA" },
  { codigo: "56", nome: "Chile", sigla: "CL" },
  { codigo: "86", nome: "China", sigla: "CN" },
  { codigo: "57", nome: "Colômbia", sigla: "CO" },
  { codigo: "82", nome: "Coreia do Sul", sigla: "KR" },
  { codigo: "506", nome: "Costa Rica", sigla: "CR" },
  { codigo: "20", nome: "Egito", sigla: "EG" },
  { codigo: "593", nome: "Equador", sigla: "EC" },
  { codigo: "34", nome: "Espanha", sigla: "ES" },
  { codigo: "1", nome: "Estados Unidos", sigla: "US" },
  { codigo: "63", nome: "Filipinas", sigla: "PH" },
  { codigo: "33", nome: "França", sigla: "FR" },
  { codigo: "31", nome: "Holanda", sigla: "NL" },
  { codigo: "91", nome: "Índia", sigla: "IN" },
  { codigo: "44", nome: "Reino Unido", sigla: "GB" },
  { codigo: "972", nome: "Israel", sigla: "IL" },
  { codigo: "39", nome: "Itália", sigla: "IT" },
  { codigo: "81", nome: "Japão", sigla: "JP" },
  { codigo: "52", nome: "México", sigla: "MX" },
  { codigo: "258", nome: "Moçambique", sigla: "MZ" },
  { codigo: "595", nome: "Paraguai", sigla: "PY" },
  { codigo: "51", nome: "Peru", sigla: "PE" },
  { codigo: "351", nome: "Portugal", sigla: "PT" },
  { codigo: "46", nome: "Suécia", sigla: "SE" },
  { codigo: "41", nome: "Suíça", sigla: "CH" },
  { codigo: "598", nome: "Uruguai", sigla: "UY" },
  { codigo: "58", nome: "Venezuela", sigla: "VE" },
];

export const soDigitos = (v: string) => (v ?? "").replace(/\D/g, "");

/** Vazio, nulo ou lixo cai em Brasil — é o que as visitas antigas são. */
export const codigoDoPais = (v: string | null | undefined): string =>
  soDigitos(v ?? "") || PAIS_PADRAO;

export const ehBrasil = (v: string | null | undefined) => codigoDoPais(v) === PAIS_PADRAO;

/** Nome para exibir. Sem achar na lista, mostra só o código. */
export function nomeDoPais(codigo: string | null | undefined): string {
  const c = codigoDoPais(codigo);
  return PAISES.find((p) => p.codigo === c)?.nome ?? `+${c}`;
}

/**
 * Máscara enquanto se digita.
 *
 * Brasil mantém a máscara de sempre, (12) 99609-3693, porque a equipe digita
 * centenas por mês e o formato ajuda a conferir. Para os outros países não dá
 * para inventar máscara — cada um tem a sua, e uma máscara errada atrapalha
 * mais que a ausência dela: separa só em grupos de quatro, para leitura.
 */
export function mascaraPorPais(valor: string, pais: string | null | undefined): string {
  const d = soDigitos(valor);
  if (ehBrasil(pais)) {
    const n = d.slice(0, 11);
    if (n.length === 0) return "";
    if (n.length <= 2) return `(${n}`;
    const corte = n.length > 10 ? 7 : 6;
    const meio = n.slice(2, corte);
    const fim = n.slice(corte);
    return fim ? `(${n.slice(0, 2)}) ${meio}-${fim}` : `(${n.slice(0, 2)}) ${meio}`;
  }
  // E.164 cabe 15 dígitos contando o código do país; sobra bastante aqui
  return (d.slice(0, 14).match(/.{1,4}/g) ?? []).join(" ");
}

/**
 * O número está completo e plausível?
 *
 * Brasil é conferido de verdade — DDD existente, celular começando em 9, fixo
 * de 2 a 5 — porque é aqui que o erro de digitação dá no número de um
 * desconhecido. Nos outros países não há como validar sem uma biblioteca de
 * regras de numeração; fica a regra do E.164: o total (país + número) não
 * passa de 15 dígitos, e o número nacional tem pelo menos 5.
 */
export function telefoneValido(numero: string, pais: string | null | undefined): boolean {
  const d = soDigitos(numero);
  if (!d) return false;
  if (ehBrasil(pais)) {
    if (!/^[1-9][1-9]/.test(d)) return false;
    if (d.length === 11) return d[2] === "9";
    if (d.length === 10) return /[2-5]/.test(d[2]);
    return false;
  }
  const c = codigoDoPais(pais);
  return d.length >= 5 && c.length + d.length <= 15;
}

/** "+55 (12) 99609-3693" — como o número aparece pronto, em lista e ficha. */
export function telefoneCompleto(
  numero: string | null | undefined,
  pais: string | null | undefined,
): string {
  const d = soDigitos(numero ?? "");
  if (!d) return "—";
  return `+${codigoDoPais(pais)} ${mascaraPorPais(d, pais)}`;
}

/**
 * Telefone para listas e telas internas.
 *
 * Mostra o código **só quando não é Brasil**. Carimbar "+55" em todas as linhas
 * gastaria largura de coluna para repetir uma informação que vale para quase
 * todas as famílias — e, pior, esconderia no meio da repetição justamente a
 * linha estrangeira, que é a que exige atenção na hora de ligar. Na ficha
 * entregue à família o código aparece sempre: ver `telefoneCompleto`.
 */
export function telefoneExibicao(
  numero: string | null | undefined,
  pais: string | null | undefined,
): string {
  const d = soDigitos(numero ?? "");
  if (!d) return "—";
  const mascarado = mascaraPorPais(d, pais);
  return ehBrasil(pais) ? mascarado : `+${codigoDoPais(pais)} ${mascarado}`;
}

/**
 * Só os dígitos que o WhatsApp precisa: código do país + número.
 *
 * Gêmeo de `paraE164` em `apps/api/src/lib/whatsapp.ts`. **Mudou aqui, muda
 * lá** — os dois pacotes não compartilham código (a API tem `rootDir: src`).
 * Se divergirem, a tela monta um link `wa.me` que leva a um número diferente
 * do que a API usou no envio automático, para a mesma família.
 */
export function paraE164(
  numero: string | null | undefined,
  pais: string | null | undefined,
): string | null {
  const c = codigoDoPais(pais);
  let d = soDigitos(numero ?? "");

  // Tolera o número migrado do 4.x que já veio com o 55 grudado na frente.
  // Sem isto a tela não montava link nenhum para esses registros, enquanto a
  // API mandava a mensagem normalmente — a mesma família, dois comportamentos.
  if (c === PAIS_PADRAO && (d.length === 12 || d.length === 13) && d.startsWith("55")) {
    d = d.slice(2);
  }
  if (!telefoneValido(d, c)) return null;
  return `${c}${d}`;
}
